import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { appRoutes } from 'app/app-routes';
import FavoriteIcons from 'common/components/FavoriteIcons';
import { imageHelper } from 'common/functions/image-helper';
import { formatters } from 'common/tools/formatters';
import { CylinderTypeValues } from 'common/types/cylinder-type';
import { DrivetrainTypeValues } from 'common/types/drivetrain-type';
import { FuelTypeValues } from 'common/types/fuel-type';
import { PageStyles } from 'common/types/PageStyles';
import { TransmissionTypeValues } from 'common/types/transmission-type';
import { CylinderType, DrivetrainType, FuelType, Listing, TransmissionType } from 'graphql/generated';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';

interface Props {
  listing: Listing;
}

const SearchResultItem = (props: Props) => {
  const navigate = useNavigate();
  const listing = props.listing;
  const [runOnce, runOnceTo] = React.useState(true);

  const user = useSelector((state: RootState) => cacheSelectors.user(state));

  const age = moment().diff(moment(listing.listingDate), 'days');
  const ageText = age === 0 ? 'Today' : age === 1 ? 'Yesterday' : `${age} days ago`;
  const [favorite, favoriteTo] = React.useState(false);

  useEffect(() => {
    if (runOnce) {
      runOnceTo(false);
      if (user?.favorites?.find(x => x.id === props.listing.id)) favoriteTo(true);
    }
  }, [props, runOnce, user]);

  const handleDetail = () => {
    navigate(`${appRoutes.itemDetail}/${listing.id}/Results`);
  };

  return (
    <>
      <Paper elevation={2} sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', left: '28%', top: '6%' }}>
          <FavoriteIcons value={favorite} toggleFn={favoriteTo} listingId={props.listing.id} user={user} />
        </Box>
        <Grid container onClick={handleDetail}>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <img
              style={{ width: '100%', height: 'auto' }}
              alt="car"
              src={imageHelper.getPrimaryImageLocation(listing.images) as string}
            />
          </Grid>
          <Grid item xs={8}>
            <Box sx={styles.body}>
              <Typography color="primary" sx={styles.title} variant="h5">
                {listing.heading}
              </Typography>
              <Typography color="primary" sx={styles.title} variant="h6">
                {listing.trim}
              </Typography>
              <Typography color="primary" sx={styles.price} variant="h6">
                {formatters.currency.format(listing.price || 0)}
              </Typography>
              <Typography color="primary" fontSize={14} sx={styles.specItemLabel} variant="body1">
                {`${listing.mileage.toLocaleString('en-US')} miles`}
              </Typography>
              <Typography sx={styles.location} variant="body1">
                Location: {`${listing.city}, ${listing.state} ${listing.zipCode}`}
              </Typography>
              <Typography sx={styles.location} variant="body1">
                Posted: {ageText}
              </Typography>
              <Box sx={styles.specs}>
                <Box sx={styles.specRow}>
                  <Box sx={styles.specItem}>
                    <Typography color="primary" sx={styles.specItemLabel} variant="body1">
                      Transmission:
                    </Typography>
                    <Typography sx={styles.specItemVal} variant="body1">
                      {TransmissionTypeValues[listing.transmissionType as TransmissionType]}
                    </Typography>
                  </Box>
                  <Box sx={styles.specItem}>
                    <Typography color="primary" sx={styles.specItemLabel} variant="body1">
                      Fuel:
                    </Typography>
                    <Typography sx={styles.specItemVal} variant="body1">
                      {FuelTypeValues[listing.fuelType as FuelType]}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={styles.specRow}>
                  <Box sx={styles.specItem}>
                    <Typography color="primary" sx={styles.specItemLabel} variant="body1">
                      Drivetrain:
                    </Typography>
                    <Typography sx={styles.specItemVal} variant="body1">
                      {DrivetrainTypeValues[listing.drivetrainType as DrivetrainType]}
                    </Typography>
                  </Box>
                  <Box sx={styles.specItem}>
                    <Typography color="primary" sx={styles.specItemLabel} variant="body1">
                      Cylinders:
                    </Typography>
                    <Typography sx={styles.specItemVal} variant="body1">
                      {CylinderTypeValues[listing.cylinderType as CylinderType]}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

const styles: PageStyles = {
  root: {
    display: 'flex',
    width: '100%',
    margin: '0 0 4px 0',
  },
  imageFrame: {
    display: 'flex',
    marginRight: 1,
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    backgroundColor: '#eaeaea',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    padding: 2,
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  price: {
    fontWeight: 500,
  },
  specHeader: {
    marginTop: 1,
    fontWeight: 500,
  },
  specs: {
    marginTop: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  specRow: {
    display: 'flex',
    width: '100%',
  },
  specItem: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '47%',
  },
  specItemLabel: {
    paddingRight: 1.5,
    width: '48%',
  },
  specItemVal: {
    width: '50%',
  },
};

export default SearchResultItem;
